import React, { useState, useEffect, useMemo, useCallback } from "react";
import PropTypes from "prop-types";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
} from "@mui/material";
import MDAvatar from "components/MDAvatar";
import DataTable from "examples/Tables/DataTable";
import MDBox from "components/MDBox";

const ProductDetailsDialog = ({ isOpen, handleClose, fetchProducts }) => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchProductData = useCallback(
    async (delay = 1000) => { // Default delay is 1s
      setLoading(true);
      setTimeout(async () => {
        try {
          const productsWithDetails = await fetchProducts();
          if (JSON.stringify(productsWithDetails) !== JSON.stringify(products)) {
            setProducts(productsWithDetails);
          }
        } catch (error) {
          console.error("Error fetching products:", error);
        } finally {
          setLoading(false);
        }
      }, delay);
    },
    [fetchProducts, products]
  );

  useEffect(() => {
    if (isOpen) {
      fetchProductData(2000);
    }
  }, [ isOpen]);

  const rows = useMemo(
    () =>
      products.map((product) => {
        return {
          productId: product?._id || "N/A",
          productName: (
            <div className="max-w-[100px] truncate">
              <p className="font-semibold" title={`${product.name.en || ''} | ${product.name.ar || ''}`}>
                {`${product.name.en || ''} | ${product.name.ar || ''}`}
              </p>
            </div>
          ),
            productimg: (
              <MDAvatar
                src={product.image || "https://via.placeholder.com/150"}
                name={product.name.en}
                size="xl"
              />
          ),
          category: `${product.category.name.en || ''} | ${product.category.name.ar || ''}`,
          description: `${product.description.en || ''} | ${product.description.ar || ''}`,
          price: `$${product?.price ?? "N/A"}`,
          stockQuantity: product?.quantity ?? "N/A",
          releaseDate: product?.createdAt
            ? new Date(product.createdAt).toLocaleDateString()
            : "N/A",
        };
      }),
    [products]
  );


  const columns = [
    { Header: "ID", accessor: "productId", align: "center" },
    { Header: "Product Image", accessor: "productimg", align: "center" },
    { Header: "Product Name", accessor: "productName", align: "center" },
    { Header: "Description", accessor: "description", align: "center" },
    { Header: "Category", accessor: "category", align: "center" },
    { Header: "Price", accessor: "price", align: "center" },
    { Header: "Stock Quantity", accessor: "stockQuantity", align: "center" },
    { Header: "Release Date", accessor: "releaseDate", align: "center" },
  ];

  return (
    <Dialog open={isOpen} onClose={handleClose} maxWidth="xl" fullWidth>
      <DialogTitle>Products</DialogTitle>
      <DialogContent>
        {loading ? (
          <div className="flex justify-center py-4">
            <CircularProgress />
          </div>
        ) : (
          <MDBox pt={3}>
            <DataTable
              table={{ columns, rows }}
              isSorted
              entriesPerPage={false}
              showTotalEntries
              noEndBorder
            />
          </MDBox>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="primary">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

ProductDetailsDialog.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  fetchProducts: PropTypes.func.isRequired,
};

export default ProductDetailsDialog;
